'use strict';

const React = require('react');
const PropTypes = require('prop-types');
const Relay = require('react-relay/classic');

const get = require('lodash.get');
const { getUserId, cookieJarConstants } = require('dibs-cookie-jar');
const { MASQUERADE_BUYER } = cookieJarConstants;
const pdpDisplayHelper = require('dibs-display-helpers/dist/pdpDisplayHelper').default;

const classNames = require('classnames');
const css = require('./ItemActionButtons-style.scss');

// Button components
const PurchaseCartButton = require('./PurchaseCartButton/PurchaseCartButton');
const MakeOfferButton = require('./MakeOfferButton/MakeOfferButton');
const RequestPriceButton = require('./RequestPriceButton/RequestPriceButton');

/**
 * ItemCTAButtons: A stateless component that returns a set of action buttons, similar to
 * ItemActionButtons, based on CTAs passed in from dibs-display-helpers.
 *
 * @param {Object} root
 * @param {Object} [root.ctas] - (Test only) preconfigured CTA flags
 * @param {Object} root.item - The item to prepare buttons for.
 * @param {string} root.itemId - The current itemId.
 * @param {Object} root.props - Props that should be passed down into the buttons.
 * @param {string} [root.containerClass] - The class name to pass down.
 * @param {string} [root.user] - User lookup result from GraphQL.
 * @returns component or null
 */
function ItemCTAButtons(inputProps) {
    const { item, itemId, user = null, props = {}, itemEligibleForProcurement } = inputProps;
    let { ctas = null, containerClass } = inputProps;
    const isOutsideSales = get(user, 'tradeFirm.isOutsideSales');

    const buyerId = getUserId(document.cookie, MASQUERADE_BUYER);

    let buyerHasTradePermission = get(user, 'canUseNetPrice');
    if (user && typeof user.canRequestNetPrice === 'function') {
        buyerHasTradePermission = user.canRequestNetPrice();
    }

    const returnValue = [];
    containerClass = containerClass || classNames(css.itemActionWidget, 'boxSizingWrapper');

    ctas =
        ctas ||
        pdpDisplayHelper({
            item: item.toJSON ? item.toJSON() : item,
            buyerId,
            buyerHasTradePermission,
        }).getShowCTAs();

    /*
     * Handle logged-in users that are loading.
     *
     * Logged-in users that have not loaded will have a truthy buyerId and falsy user object.
     * Logged-in users that have loaded will have truthy buyerId and user object.
     * Logged-out users will have a falsy buyerId and user object.
     *
     * If a logged-in user is waiting for the GraphQL call to complete, reserve space based on the
     * item's requirements.  The item will always determine the number of buttons to be displayed,
     * while the user object (and the buyerHasTradePermission derived from the user object) dictates
     * which buttons will be displayed.
     */
    if (!user && buyerId) {
        let placeholder = css.placeholderOneButton;
        if (!ctas.showRequestCTA && ctas.showPurchaseCTA) {
            placeholder = css.placeholderTwoButtons;
        }
        containerClass = classNames(css.itemActionWidget, placeholder, 'boxSizingWrapper');
    } else {
        if (ctas.showRequestCTA) {
            // Since this is referenced differently in its contexts (PDP sidebar, favorites),
            // it will provide its own padding.
            return <RequestPriceButton {...props} />;
        }

        if (ctas.showPurchaseCTA) {
            returnValue.push(<PurchaseCartButton key={`purchaseCart-${itemId}`} {...props} />);
        }

        // Given that "Make Offer" CTA (ctas.showMakeOfferCTA) is true, it should be displayed here if 1 of the following is also true:
        // 1) item IS NOT Furniture/Art aka (!itemEligibleForProcurement) or
        // 2) item IS Furniture/Art (itemEligibleForProcurement) and user/trade firm IS NOT Tier 1/Tier 2 (!isOutsideSales)
        // Otherwise, if item IS Furniture/Art and user/trade firm IS Tier 1/Tier 2, it should be shown
        // in bottom "Contact Dealer" section of Sidebar (See ContactDealerSidebar.jsx)
        if (
            ctas.showMakeOfferCTA &&
            (!itemEligibleForProcurement || (itemEligibleForProcurement && !isOutsideSales))
        ) {
            returnValue.push(<MakeOfferButton key={`makeOffer-${itemId}`} {...props} />);
        }
    }

    return <div className={containerClass}>{returnValue}</div>;
}

ItemCTAButtons.propTypes = {
    ctas: PropTypes.object,
    itemId: PropTypes.string.isRequired,
    item: PropTypes.object.isRequired,
    containerClass: PropTypes.string,
    props: PropTypes.object,
    user: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    wrapResult: PropTypes.bool,
    itemEligibleForProcurement: PropTypes.bool,
};

ItemCTAButtons.contextTypes = {
    executeAction: PropTypes.func.isRequired,
};

const Connected = Relay.createContainer(ItemCTAButtons, {
    fragments: {
        user: () => Relay.QL`
            fragment on User {
                canUseNetPrice
                tradeFirm {
                    isOutsideSales
                }
            }
        `,
    },
});

module.exports = {
    Connected,
    Raw: ItemCTAButtons,
};



// WEBPACK FOOTER //
// ./assets/js/components/ItemActionButtons/ItemCTAButtons.jsx